import React, { Component } from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

// modules
import * as d3 from 'd3';

// ui/ux
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// ours
import Waiting from '../Waiting';
import { uniqueId, convertSnakeCase } from '../../common';

// GLOBALS
const GRAPH_RADIUS_PERC = 0.08; /* percent of the width */

const styles = (/* theme */) => ({
  svg: {
    '& text#raw': {
      display: 'none'
    },
    '&:hover text#perc': {
      display: 'none'
    },
    '&:hover text#raw': {
      display: 'initial'
    }
  }
});

class MSPieChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      id: `${uniqueId()}`
    };
    this.componentDidMount = this.componentDidUpdate;
  }
  componentDidUpdate() {
    const { width, theme } = this.props;
    const { id } = this.state;

    const data = this.props.data;
    const svg = d3.select(`#${id}`);

    if (svg.size() === 0) {
      return;
    }

    svg.html('');

    if (!data) {
      return;
    }
    const spacing = theme.spacing(2);
    let n = '__N__' in data ? data.__N__ : Object.values(data).reduce((a, b) => Math.max(a, b));
    if (n === 0) n = 1;
    const cats = Object.keys(data)
      .filter((d) => d !== '__N__')
      .sort((a, b) => {
        if (data[a] / n === data[b] / n) {
          return 0;
        }
        if (data[a] / n < data[b] / n) {
          return 1;
        } else {
          return -1;
        }
      });

    const radius = width * GRAPH_RADIUS_PERC;
    const diameter = radius << 1;

    for (let i = 0; i < cats.length; i++) {
      const p = Math.round((data[cats[i]] / n) * 10000.0) / 100.0;
      const colors = [/* p < 40 ? alpha(*/ theme.palette.secondary.main /* , 0.5) : theme.palette.secondary.main*/, theme.palette.primary.main];
      const color = d3.scaleOrdinal(colors);
      const subData = [
        {
          k: 'on',
          v: p
        },
        {
          k: 'off',
          v: 100 - p
        }
      ];
      const stepping = theme.spacing(1) * (i + 1);
      // const ty = (spacing * (i + 1)) + radius + (diameter * i);
      const ty = i * diameter + radius + stepping;
      const g = svg.append('g').attr('transform', `translate(${spacing + radius}, ${ty})`);

      g.append('text')
        .text(`${p}%`)
        .attr('id', 'perc')
        .attr('font-weight', 'bold')
        .attr('font-size', `${radius / 2.5}px`)
        .attr('text-anchor', 'middle')
        .attr('fill', colors[0])
        .attr('alignment-baseline', 'middle');

      g.append('text')
        .text(`${data[cats[i]]}`)
        .attr('id', 'raw')
        .attr('font-weight', 'bold')
        .attr('font-size', `${radius / 2.5}px`)
        .attr('text-anchor', 'middle')
        .attr('fill', colors[0])
        .attr('alignment-baseline', 'middle');

      g.append('text')
        .text(`${n}`)
        .attr('id', 'raw')
        .attr('y', `${radius / 2.5}px`)
        .attr('font-weight', 'bold')
        .attr('font-size', `${radius / 5}px`)
        .attr('text-anchor', 'middle')
        .attr('fill', colors[0])
        .attr('alignment-baseline', 'middle');

      const fow = width - diameter - spacing * 2;

      const fo = g
        .append('foreignObject')
        .attr('x', radius + spacing)
        .attr('y', -radius)
        .attr('height', radius * 2)
        .attr('width', fow);

      fo.append('xhtml:div')
        .attr('xmlns', 'http://www.w3.org/1999/xhtml')
        .attr('style', `display: table-cell; text-align: left; vertical-align: middle; height: ${radius * 2}px; word-break: break-all;`)
        .text(convertSnakeCase(cats[i]));

      const pie = d3
        .pie()
        .value((d) => d.v)
        .sort(null);

      const arc = d3
        .arc()
        .innerRadius(radius * 0.75)
        .outerRadius(radius);

      const path = g.selectAll('path').data(pie(subData));
      path
        .enter()
        .append('path')
        .attr('fill', (d, i) => color(i))
        .attr('d', arc)
        .attr('stroke', theme.palette.getContrastText(theme.palette.background.paper))
        .attr('stroke-width', '1px');
    }
  }

  render() {
    const { classes, theme, data, title, width } = this.props;
    const { id } = this.state;

    if (!data) {
      return (
        <Box
          style={{
            width,
            height: width
          }}
        >
          <Typography variant="h6">{convertSnakeCase(title)}</Typography>
          <Waiting />
        </Box>
      );
    }

    const l = Math.max(Object.keys(data).length, 0);

    const diameter = width * GRAPH_RADIUS_PERC * 2.0;
    const height = (theme.spacing(1) + diameter) * l;

    return (
      <Box>
        <Typography variant="h6">{convertSnakeCase(title)}</Typography>
        <svg id={id} className={classes.svg} width={width} height={height} />
      </Box>
    );
  }
}

MSPieChart.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  data: PropTypes.object,
  title: PropTypes.string,

  width: PropTypes.number
};

MSPieChart.defaultProps = {
  width: 400
};

export default withTheme(withStyles(styles)(MSPieChart));
